import React, { useState, useEffect } from 'react';
import { Calendar, Star, Clock, ChevronLeft, ChevronRight, Eye, Download, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ArchivedHoroscope {
  date: string;
  available: boolean;
  signes?: any[];
}

const AstroDailyArchives: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>('2025-08-08');
  const [horoscopeData, setHoroscopeData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);

  // Générer la liste des dates disponibles (5 juin au 8 août 2025)
  useEffect(() => {
    const dates: string[] = [];
    const startDate = new Date('2025-06-05');
    const endDate = new Date('2025-08-08');
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }
    
    setAvailableDates(dates);
  }, []);

  // Charger les données d'une date spécifique
  const loadHoroscope = async (date: string) => {
    setLoading(true);
    try {
      // Essayer de charger depuis les archives
      const response = await fetch(`/archives/astrodaily/${date}/today.json`);
      if (response.ok) {
        const data = await response.json();
        setHoroscopeData(data);
      }
    } catch (error) {
      console.error('Erreur chargement horoscope:', error);
      setHoroscopeData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedDate) {
      loadHoroscope(selectedDate);
    }
  }, [selectedDate]);

  const navigateDate = (direction: number) => {
    const currentIndex = availableDates.indexOf(selectedDate);
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < availableDates.length) {
      setSelectedDate(availableDates[newIndex]);
    }
  };

  const zodiacSigns = [
    { id: 'belier', name: 'Bélier', emoji: '♈', element: 'feu' },
    { id: 'taureau', name: 'Taureau', emoji: '♉', element: 'terre' },
    { id: 'gemeaux', name: 'Gémeaux', emoji: '♊', element: 'air' },
    { id: 'cancer', name: 'Cancer', emoji: '♋', element: 'eau' },
    { id: 'lion', name: 'Lion', emoji: '♌', element: 'feu' },
    { id: 'vierge', name: 'Vierge', emoji: '♍', element: 'terre' },
    { id: 'balance', name: 'Balance', emoji: '♎', element: 'air' },
    { id: 'scorpion', name: 'Scorpion', emoji: '♏', element: 'eau' },
    { id: 'sagittaire', name: 'Sagittaire', emoji: '♐', element: 'feu' },
    { id: 'capricorne', name: 'Capricorne', emoji: '♑', element: 'terre' },
    { id: 'verseau', name: 'Verseau', emoji: '♒', element: 'air' },
    { id: 'poissons', name: 'Poissons', emoji: '♓', element: 'eau' }
  ];

  const getElementColor = (element: string) => {
    switch(element) {
      case 'feu': return '#FF6B6B';
      case 'terre': return '#8B7355';
      case 'air': return '#87CEEB';
      case 'eau': return '#4682B4';
      default: return '#9333EA';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0A0A0B 0%, #1A1A2E 100%)',
      color: '#FAFAFA',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header avec navigation */}
      <header style={{
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #27272A',
        padding: '1rem 2rem',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Link to="/" style={{
            color: '#FAFAFA',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <ChevronLeft size={20} />
            Retour à l'accueil
          </Link>
          
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            background: 'linear-gradient(90deg, #9333EA, #EC4899)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            📚 Archives AstroDaily
          </h1>
          
          <div style={{ width: '150px' }} />
        </div>
      </header>

      {/* Zone de mémorial */}
      <div style={{
        maxWidth: '1200px',
        margin: '3rem auto',
        padding: '0 2rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1), rgba(236, 72, 153, 0.1))',
          border: '2px solid rgba(147, 51, 234, 0.3)',
          borderRadius: '20px',
          padding: '2rem',
          marginBottom: '3rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: -50,
            right: -50,
            fontSize: '200px',
            opacity: 0.05,
            transform: 'rotate(-15deg)'
          }}>
            🌟
          </div>
          
          <h2 style={{
            fontSize: '2rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <Heart size={32} color="#EC4899" />
            In Memoriam : AstroDaily (5 juin - 8 août 2025)
          </h2>
          
          <p style={{
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: '#A1A1AA',
            marginBottom: '1.5rem'
          }}>
            Pendant <strong style={{ color: '#FAFAFA' }}>58 jours</strong>, AstroDaily a illuminé le quotidien avec des prédictions mystiques 
            alimentées par de vraies données astronomiques. Un système <strong style={{ color: '#9333EA' }}>révolutionnaire</strong> qui 
            combinait l'art du mentalisme avec la précision de l'IA, créant plus de <strong style={{ color: '#EC4899' }}>20'000 prédictions 
            personnalisées</strong> et <strong style={{ color: '#F59E0B' }}>696 cadrans de montres cosmiques</strong>.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginTop: '2rem'
          }}>
            <div style={{
              background: 'rgba(147, 51, 234, 0.1)',
              padding: '1rem',
              borderRadius: '10px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>58</div>
              <div style={{ color: '#A1A1AA', fontSize: '0.9rem' }}>Jours de service</div>
            </div>
            <div style={{
              background: 'rgba(236, 72, 153, 0.1)',
              padding: '1rem',
              borderRadius: '10px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>696</div>
              <div style={{ color: '#A1A1AA', fontSize: '0.9rem' }}>Images générées</div>
            </div>
            <div style={{
              background: 'rgba(245, 158, 11, 0.1)',
              padding: '1rem',
              borderRadius: '10px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>1.3 GB</div>
              <div style={{ color: '#A1A1AA', fontSize: '0.9rem' }}>Données archivées</div>
            </div>
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              padding: '1rem',
              borderRadius: '10px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>100%</div>
              <div style={{ color: '#A1A1AA', fontSize: '0.9rem' }}>Uptime garanti</div>
            </div>
          </div>
        </div>

        {/* Sélecteur de date */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '15px',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid #27272A'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <button
              onClick={() => navigateDate(-1)}
              disabled={availableDates.indexOf(selectedDate) === 0}
              style={{
                background: 'rgba(147, 51, 234, 0.2)',
                border: '1px solid #9333EA',
                borderRadius: '10px',
                padding: '0.5rem 1rem',
                color: '#FAFAFA',
                cursor: availableDates.indexOf(selectedDate) === 0 ? 'not-allowed' : 'pointer',
                opacity: availableDates.indexOf(selectedDate) === 0 ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <ChevronLeft size={20} />
              Jour précédent
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              fontSize: '1.2rem'
            }}>
              <Calendar size={24} color="#9333EA" />
              <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid #27272A',
                  borderRadius: '8px',
                  padding: '0.5rem 1rem',
                  color: '#FAFAFA',
                  fontSize: '1rem'
                }}
              >
                {availableDates.map(date => (
                  <option key={date} value={date}>
                    {new Date(date + 'T12:00:00').toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => navigateDate(1)}
              disabled={availableDates.indexOf(selectedDate) === availableDates.length - 1}
              style={{
                background: 'rgba(147, 51, 234, 0.2)',
                border: '1px solid #9333EA',
                borderRadius: '10px',
                padding: '0.5rem 1rem',
                color: '#FAFAFA',
                cursor: availableDates.indexOf(selectedDate) === availableDates.length - 1 ? 'not-allowed' : 'pointer',
                opacity: availableDates.indexOf(selectedDate) === availableDates.length - 1 ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Jour suivant
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Grille des horoscopes */}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem',
            fontSize: '1.2rem',
            color: '#A1A1AA'
          }}>
            <Clock className="animate-spin" size={48} style={{ margin: '0 auto 1rem' }} />
            Chargement des archives...
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {zodiacSigns.map((sign, index) => {
              const horoscope = horoscopeData?.horoscopes?.[index];
              return (
                <div
                  key={sign.id}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: `2px solid ${getElementColor(sign.element)}`,
                    borderRadius: '15px',
                    padding: '1.5rem',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = `0 10px 30px rgba(147, 51, 234, 0.3)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Image du cadran */}
                  <div style={{
                    width: '100%',
                    height: '200px',
                    background: `url(/archives/astrodaily/${selectedDate}/${sign.id}.png) center/cover`,
                    borderRadius: '10px',
                    marginBottom: '1rem',
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: getElementColor(sign.element),
                      color: 'white',
                      padding: '0.3rem 0.8rem',
                      borderRadius: '20px',
                      fontSize: '1.5rem'
                    }}>
                      {sign.emoji}
                    </div>
                  </div>

                  <h3 style={{
                    fontSize: '1.3rem',
                    marginBottom: '0.5rem',
                    color: getElementColor(sign.element)
                  }}>
                    {sign.name}
                  </h3>

                  {horoscope && (
                    <>
                      <p style={{
                        fontSize: '0.9rem',
                        color: '#A1A1AA',
                        lineHeight: '1.5',
                        marginBottom: '1rem'
                      }}>
                        {horoscope.fr?.texte_court_fr || 'Horoscope archivé'}
                      </p>

                      {/* Stats d'énergie */}
                      {horoscope.energy_stats && (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '0.5rem',
                          marginTop: '1rem'
                        }}>
                          {Object.entries(horoscope.energy_stats).map(([key, value]: [string, any]) => (
                            <div key={key} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontSize: '0.85rem'
                            }}>
                              <div style={{
                                width: '60px',
                                fontSize: '0.75rem',
                                color: '#A1A1AA'
                              }}>
                                {key === 'amour' ? '❤️' : key === 'travail' ? '💼' : key === 'sante' ? '🏥' : '🍀'} {key}
                              </div>
                              <div style={{
                                flex: 1,
                                height: '6px',
                                background: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '3px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${value}%`,
                                  height: '100%',
                                  background: `linear-gradient(90deg, ${getElementColor(sign.element)}, ${getElementColor(sign.element)}99)`,
                                  borderRadius: '3px'
                                }} />
                              </div>
                              <span style={{ fontSize: '0.75rem', color: '#A1A1AA' }}>{value}%</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Numéros chance */}
                      {horoscope.lucky_numbers && (
                        <div style={{
                          marginTop: '1rem',
                          display: 'flex',
                          gap: '0.5rem',
                          alignItems: 'center'
                        }}>
                          <Star size={16} color="#F59E0B" />
                          <span style={{ fontSize: '0.85rem', color: '#A1A1AA' }}>
                            Numéros chance : {horoscope.lucky_numbers.join(', ')}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Message de fin */}
        <div style={{
          textAlign: 'center',
          marginTop: '4rem',
          padding: '2rem',
          color: '#A1A1AA',
          fontSize: '0.9rem',
          lineHeight: '1.6'
        }}>
          <p style={{ marginBottom: '1rem' }}>
            Ces archives représentent 58 jours d'innovation et de créativité automatisée.
          </p>
          <p>
            AstroDaily restera dans l'histoire comme le premier système à combiner véritables données astronomiques 
            avec l'art ancestral du mentalisme, le tout orchestré par une IA de dernière génération.
          </p>
          <p style={{ marginTop: '2rem', fontSize: '1.1rem', color: '#9333EA' }}>
            "Les étoiles se souviennent de tout." ✨
          </p>
        </div>
      </div>
    </div>
  );
};

export default AstroDailyArchives;